import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartPieIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { 
  PieChart, 
  Pie, 
  Cell, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import { downloadFile } from '../utils/exportUtils';
import { useUser } from '../contexts/UserContext';

const ExecutiveSummary = () => {
  const { isAdmin } = useUser();
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState('current-month');
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);

  // Predefined date range options
  const dateRangeOptions = [
    { value: 'current-month', label: 'Current Month', getDates: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date();
      return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
    }},
    { value: 'last-month', label: 'Last Month', getDates: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
    }},
    { value: 'last-3-months', label: 'Last 3 Months', getDates: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      const end = new Date();
      return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
    }},
    { value: 'last-6-months', label: 'Last 6 Months', getDates: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 6, 1);
      const end = new Date();
      return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
    }},
    { value: 'last-12-months', label: 'Last 12 Months', getDates: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 12, 1);
      const end = new Date();
      return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
    }}
  ];

  useEffect(() => {
    const loadTeams = async () => {
      try {
        const userId = isAdmin() ? null : authService.getUserId();
        const teamsData = await apiService.getTeams(userId);
        setTeams(teamsData);
      } catch (err) {
        console.error('Failed to load teams:', err);
      }
    };
    loadTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadExecutiveSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedTeamId]);

  const loadExecutiveSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const selectedOption = dateRangeOptions.find(opt => opt.value === dateRange);
      const { startDate, endDate } = selectedOption.getDates();
      
      console.log('Loading executive summary for date range:', { startDate, endDate });
      
      // Load all three reports data - using the same pattern as other working reports
      const slaData = await apiService.getSlaReport(selectedTeamId);
      const churnData = await apiService.getTicketChurnReport(startDate, endDate, selectedTeamId);
      const categoryData = await apiService.getTicketCategoryBreakdown(new Date(startDate), new Date(endDate), selectedTeamId);

      console.log('SLA Data:', slaData);
      console.log('Churn Data:', churnData);
      console.log('Category Data:', categoryData);

      // Combine and process the data
      const summary = {
        sla: slaData,
        churn: churnData,
        categories: categoryData,
        generatedAt: new Date().toISOString(),
        dateRange: {
          start: startDate,
          end: endDate
        }
      };

      console.log('Combined summary data:', summary);
      setSummaryData(summary);
    } catch (err) {
      console.error('Failed to load executive summary:', err);
      setError('Failed to load executive summary. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (e) => {
    setDateRange(e.target.value);
  };

  const handleGenerateReport = () => {
    loadExecutiveSummary();
  };

  const handleExportToExcel = async () => {
    try {
      setExporting(true);
      // For now, export the summary data as JSON
      // TODO: Implement proper Excel export for combined data
      const dataStr = JSON.stringify(summaryData, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const selectedOption = dateRangeOptions.find(opt => opt.value === dateRange);
      const { startDate, endDate } = selectedOption.getDates();
      const filename = `Executive_Summary_${startDate}_to_${endDate}.json`;
      downloadFile(blob, filename);
    } catch (err) {
      console.error('Failed to export executive summary:', err);
      setError('Failed to export report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-600">{error}</p>
        <button
          onClick={loadExecutiveSummary}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!summaryData) {
    return (
      <div className="text-center py-12">
        <ChartPieIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No data available</h3>
        <p className="mt-1 text-sm text-gray-500">
          No executive summary data available for the selected period.
        </p>
      </div>
    );
  }

  // Get current date range for display
  const selectedOption = dateRangeOptions.find(opt => opt.value === dateRange);
  const { startDate, endDate } = selectedOption.getDates();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Executive Summary Report</h1>
          <p className="text-gray-600">
            Comprehensive overview of SLA performance, ticket trends, and category distribution
          </p>
        </div>
        <Link
          to="/reports"
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Reports
        </Link>
      </div>

      {/* Date Range Controls */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <ClockIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Date Range:</span>
          </div>
          
          <select
            value={dateRange}
            onChange={handleDateRangeChange}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {dateRangeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {teams.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">Team:</span>
              <select
                value={selectedTeamId || ''}
                onChange={(e) => setSelectedTeamId(e.target.value ? parseInt(e.target.value) : null)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Teams</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>
          )}

          <button
            onClick={handleGenerateReport}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Generate Report
          </button>
          
          <button
            onClick={handleExportToExcel}
            disabled={exporting}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-2 inline" />
            {exporting ? 'Exporting...' : 'Export'}
          </button>
        </div>
        
        <div className="mt-3 text-sm text-gray-600">
          Showing data from {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
        </div>
      </div>

      {/* Executive Summary Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* SLA Performance Card */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">SLA Performance</h3>
          </div>
          
          {summaryData.sla && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {summaryData.sla.summary?.overallComplianceRate?.toFixed(1) || 'N/A'}%
                </div>
                <div className="text-sm text-gray-500">Overall Compliance</div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Meeting SLA:</span>
                  <span className="font-medium text-green-600">
                    {summaryData.sla.summary?.metSla || 0}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Breaching SLA:</span>
                  <span className="font-medium text-red-600">
                    {summaryData.sla.summary?.breachedSla || 0}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Ticket Volume Card */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <ArrowTrendingUpIcon className="h-6 w-6 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Ticket Volume</h3>
          </div>
          
          {summaryData.churn && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {summaryData.churn.summary?.totalTicketsCreated || 0}
                </div>
                <div className="text-sm text-gray-500">Total Tickets</div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>New Tickets:</span>
                  <span className="font-medium text-blue-600">
                    {summaryData.churn.summary?.totalTicketsCreated || 0}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Closed:</span>
                  <span className="font-medium text-green-600">
                    {summaryData.churn.summary?.totalTicketsClosed || 0}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Category Distribution Card */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <ChartPieIcon className="h-6 w-6 text-purple-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Top Category</h3>
          </div>
          
          {summaryData.categories && summaryData.categories.categories && summaryData.categories.categories.length > 0 && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">
                  {summaryData.categories.categories[0]?.categoryName || 'N/A'}
                </div>
                <div className="text-sm text-gray-500">
                  {summaryData.categories.categories[0]?.percentage?.toFixed(1) || 0}% of tickets
                </div>
              </div>
              
              <div className="text-center text-sm text-gray-600">
                {summaryData.categories.categories[0]?.ticketCount || 0} tickets
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detailed Analysis Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* SLA Analysis */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">SLA Breach Analysis</h3>
          </div>
          
          {summaryData.sla && summaryData.sla.summary && summaryData.sla.summary.breachedSla > 0 ? (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 mb-4">
                <strong>Key Insights:</strong> {summaryData.sla.summary.breachedSla} tickets breached SLA during this period.
              </div>
              
              <div className="space-y-3">
                <div className="border-l-4 border-red-500 pl-3">
                  <div className="text-sm font-medium text-gray-900">
                    SLA Breach Summary
                  </div>
                  <div className="text-xs text-gray-500">
                    {summaryData.sla.summary.breachedSla} tickets breached SLA | Overall compliance: {summaryData.sla.summary.overallComplianceRate?.toFixed(1) || 0}%
                  </div>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-red-50 rounded-md">
                <div className="text-sm text-red-800">
                  <strong>Recommendation:</strong> Focus on improving response times for high-priority tickets to reduce SLA breaches.
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <CheckCircleIcon className="mx-auto h-12 w-12 text-green-400 mb-2" />
              <p>No SLA breaches in this period!</p>
            </div>
          )}
        </div>

        {/* Churn Analysis */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <ArrowTrendingDownIcon className="h-6 w-6 text-orange-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Ticket Churn Analysis</h3>
          </div>
          
          {summaryData.churn && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 mb-4">
                <strong>Trend Analysis:</strong> Ticket volume patterns and resolution efficiency.
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span>Average Resolution Time:</span>
                  <span className="font-medium">
                    {summaryData.churn.summary?.averageResolutionTimeHours ? 
                      `${Math.round(summaryData.churn.summary.averageResolutionTimeHours)} hours` : 'N/A'
                    }
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Resolution Rate:</span>
                  <span className="font-medium text-green-600">
                    {summaryData.churn.summary?.resolutionRate ? 
                      `${summaryData.churn.summary.resolutionRate.toFixed(1)}%` : 'N/A'
                    }
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Breach Rate:</span>
                  <span className="font-medium text-red-600">
                    {summaryData.churn.summary?.breachRate ? 
                      `${summaryData.churn.summary.breachRate.toFixed(1)}%` : 'N/A'
                    }
                  </span>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-blue-50 rounded-md">
                <div className="text-sm text-blue-800">
                  <strong>Insight:</strong> {summaryData.churn.summary?.totalTicketsCreated > summaryData.churn.summary?.totalTicketsClosed ? 
                    'Ticket volume is increasing. Consider adding resources.' : 
                    'Resolution rate is keeping pace with new tickets.'
                  }
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Category Insights */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-4">
          <ChartPieIcon className="h-6 w-6 text-purple-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Category Distribution Insights</h3>
        </div>
        
        {summaryData.categories && summaryData.categories.categories && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pie Chart */}
            <div className="flex justify-center">
              <div style={{ width: '300px', height: '300px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={summaryData.categories.categories}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="ticketCount"
                      nameKey="categoryName"
                      labelLine={false}
                    >
                      {summaryData.categories.categories.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name, props) => [`${value} tickets`, props.payload.categoryName]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Category Analysis */}
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                <strong>Distribution Analysis:</strong> Understanding ticket spread across categories helps identify focus areas.
              </div>
              
              <div className="space-y-3">
                {summaryData.categories.categories.map((category, index) => (
                  <div key={category.categoryId} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded mr-2"
                        style={{ backgroundColor: category.color }}
                      ></div>
                      <span className="text-sm font-medium text-gray-900">
                        {category.categoryName}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-900">
                        {category.ticketCount}
                      </div>
                      <div className="text-xs text-gray-500">
                        {category.percentage?.toFixed(1) || 0}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 p-3 bg-purple-50 rounded-md">
                <div className="text-sm text-purple-800">
                  <strong>Recommendation:</strong> {summaryData.categories.categories[0]?.categoryName} represents the highest volume. 
                  Consider specialized training or dedicated resources for this category.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Executive Recommendations */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <InformationCircleIcon className="h-6 w-6 text-blue-600 mr-2" />
          <h3 className="text-lg font-semibold text-blue-900">Executive Recommendations</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <h4 className="font-medium text-blue-800">Immediate Actions</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              {summaryData.sla && summaryData.sla.summary && summaryData.sla.summary.breachedSla > 0 && (
                <li>• Address {summaryData.sla.summary.breachedSla} SLA breaches</li>
              )}
              {summaryData.churn && summaryData.churn.summary && summaryData.churn.summary.totalTicketsCreated > summaryData.churn.summary.totalTicketsClosed && (
                <li>• Monitor increasing ticket volume trend</li>
              )}
              <li>• Review resource allocation for high-volume categories</li>
            </ul>
          </div>
          
          <div className="space-y-3">
            <h4 className="font-medium text-blue-800">Strategic Focus</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Improve response times for high-priority tickets</li>
              <li>• Optimize workflow for {summaryData.categories?.categories?.[0]?.categoryName || 'top'} category</li>
              <li>• Implement proactive SLA monitoring</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Report Metadata */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="text-sm text-gray-600 text-center">
          Executive Summary Report generated at {new Date(summaryData.generatedAt).toLocaleString()}
        </div>
      </div>
    </div>
  );
};

export default ExecutiveSummary;
